import { TicketOptionWithQuantity, ProductInstance, Translations } from '@repo/types';
import { QuantityError, ValidationContext, QuantityInputItem } from '../shared/types';
import {
    validateIndividualMinEntrants,
    validateIndividualCapacity,
    validateCollectionMinEntrants,
    validateCollectionCapacity,
    validateCollectionResourceCapacity,
    validateJoinedTripCapacity,
    validateJoinedTripMinEntrants,
    validateSharedProductInstanceCapacity,
    validateSharedInstanceMinEntrants,
    validateNotEmpty,
} from '../shared/validation-rules';
import {
    calculateBaseMax,
    calculateSharedInstanceMax,
    calculateSharedInstanceMin,
    calculateCollectionMax,
    calculateCollectionResourceMax,
    calculateJoinedTripMax,
    calculateMin,
} from '../shared/capacity-calculators';
import { isUnboundedCapacity } from '../shared/utils';
import {
    getTicketName,
    getAgeRangeText,
    getErrorForTicket,
    capitalize,
} from '../shared/transformers';

export const buildActivityContext = (
    tickets: TicketOptionWithQuantity[],
    selectedProducts: ProductInstance[],
): ValidationContext => {
    // If selectedProducts is empty, extract from tickets
    // This handles test cases where selectedProducts isn't explicitly provided
    const productInstances =
        selectedProducts.length > 0 ? selectedProducts : tickets.flatMap((t) => t.productInstances);

    // Detect modes from product instances
    const hasCollectionMode = productInstances.some(
        (pi) => pi.productInstanceCollectionId !== null,
    );

    const hasJoinedTrip = productInstances.some(
        (pi) => typeof pi.productInstanceCollectionMinEntrants === 'number',
    );

    const requireAllValid = productInstances.some(
        (pi) => pi.productInstanceCollectionValidateAll === true,
    );

    // Individual validation is SKIPPED only in Case 4: hasCollectionMode && hasJoinedTrip && !requireAllValid
    const shouldValidateIndividual = !(hasCollectionMode && hasJoinedTrip && !requireAllValid);

    // Extract capacity limits from first product instance (they should be the same across instances)
    const [firstPI] = productInstances;

    const maxEntrantsPerBooking = firstPI?.productInstanceCollectionMaxEntrantsPerBooking ?? null;

    const globalMaxEntrants = firstPI?.productInstanceCollectionMaxEntrants ?? null;

    const collectionMinEntrants = firstPI?.productInstanceCollectionMinEntrants ?? null;

    // Calculate current consumed capacity (sum across all tickets)
    const currentConsumedCapacity = tickets.reduce(
        (sum, ticket) => sum + (ticket.currentConsumedCapacity ?? 0),
        0,
    );

    // SB-933: when the per-availability `capacity` field genuinely represents
    // seats on a shared resource pool, that value must bind as a TOTAL across
    // sibling ticket options — otherwise 5+5=10 slips through on a 5-seat bus.
    // Sourced from selectedProducts[0] (the merged PI) at runtime; in
    // non-collection mode there is no merged PI, so leave null and let the
    // regular shared-instance machinery handle distribution. Unbounded
    // sentinels are treated as no-constraint.
    //
    // The wrinkle: Bilberry exposes no flag that directly says "per-availability
    // capacity IS a shared resource pool." What `capacity` means depends on
    // which physical resource(s) the operator bound each availability to via
    // the per-price `Ressurs` field (boat, climbing course, cabin, bus, …):
    //   - Siblings on the SAME resource (Mushroom Hunt: adult+child on one
    //     bus) coincidentally report identical capacities — that's what made
    //     SB-933's "5+5=10 on a 5-seat bus" symptom appear.
    //   - Siblings on DIFFERENT resources (climbing: Green vs Blue/Red course;
    //     accommodation: cabin unit vs occupancy slots) report different
    //     capacities; summing them is meaningless.
    //
    // SB-944/SB-956: gate on requireAllValid as a PROXY. The flag itself
    // literally controls whether per-category min_entrants is enforced at zero
    // quantity — see validateIndividualMinEntrants, validateSharedInstance-
    // MinEntrants, validateJoinedTripMinEntrants. The operator-facing UI
    // tooltip is "Alle billetter skal valideres" ("All tickets must be
    // validated"), and `joined_mode` is the orthogonal "use global min/max
    // instead of per-ticket min/max" toggle. Neither flag formally signals
    // shared-pool semantics. But operator convention correlates require_all_-
    // valid with shared-pool intent:
    //   - require_all_valid=1: operators who think of a product as one
    //     coordinated booking also tend to want every category required and
    //     bind every price to the same resource (SB-933: Mushroom Hunt, both
    //     sibling caps = 5).
    //   - require_all_valid=0: operators who think of categories as
    //     independent dimensions leave them optional and bind prices to
    //     different resources (SB-944: accommodation Unit count 1 vs extras 4;
    //     SB-956: climbing park children's Green cap 49 vs adults' Blue/Red
    //     cap 170, both inside the same joined trip with collection max 223).
    //
    // The proxy isn't a strict Bilberry invariant — a hypothetical operator
    // could configure a shared-resource joined trip with require_all_valid=0
    // and re-introduce the SB-933 overage. No such product is known in
    // production today; if one appears, the principled fix would be a
    // dedicated backend signal (e.g. exposing `Ressurs` per price in the API
    // and detecting shared bindings) rather than another widget-side heuristic.
    const rawSharedResourceCapacity = firstPI?.capacity ?? null;
    const sharedResourceCapacity =
        hasCollectionMode &&
        requireAllValid &&
        rawSharedResourceCapacity !== null &&
        !isUnboundedCapacity(rawSharedResourceCapacity)
            ? rawSharedResourceCapacity
            : null;

    return {
        hasCollectionMode,
        hasJoinedTrip,
        requireAllValid,
        shouldValidateIndividual,
        maxEntrantsPerBooking,
        globalMaxEntrants,
        collectionMinEntrants,
        currentConsumedCapacity,
        sharedResourceCapacity,
        productInstances,
    };
};

export const validateActivityQuantities = (
    tickets: TicketOptionWithQuantity[],
    context: ValidationContext,
    attemptedBooking = false,
): QuantityError[] => {
    const errors: QuantityError[] = [];

    // 1. Validate empty quantities (if booking attempted)
    errors.push(...validateNotEmpty(tickets, attemptedBooking));

    // 2. Individual validation (per ticket)
    if (context.shouldValidateIndividual) {
        for (const ticket of tickets) {
            errors.push(...validateIndividualMinEntrants(ticket, context));
            errors.push(...validateIndividualCapacity(ticket, context));
        }
    }

    // 3. Shared product instance validation (TOTAL-based for minEntrants and capacity)
    errors.push(...validateSharedInstanceMinEntrants(tickets, context));
    errors.push(...validateSharedProductInstanceCapacity(tickets, context));

    // 4. Collection mode validation (total across all tickets)
    if (context.hasCollectionMode) {
        errors.push(...validateCollectionMinEntrants(tickets, context));
        errors.push(...validateCollectionCapacity(tickets, context));
        // SB-933: per-entry capacity binds total across siblings.
        errors.push(...validateCollectionResourceCapacity(tickets, context));
    }

    // 5. Joined trip validation (global capacity + minEntrants)
    if (context.hasJoinedTrip) {
        errors.push(...validateJoinedTripCapacity(tickets, context));
        errors.push(...validateJoinedTripMinEntrants(tickets, context));
    }

    return errors;
};

export const calculateActivityMax = (
    ticket: TicketOptionWithQuantity,
    allTickets: TicketOptionWithQuantity[],
    context: ValidationContext,
): number => {
    // In non-collection/non-joined-trip mode, use shared instance distribution
    // In collection/joined trip mode, use base calculation (distribution handled separately)
    let max;
    if (!context.hasCollectionMode && !context.hasJoinedTrip) {
        // Shared instance distribution for regular activities
        max = calculateSharedInstanceMax(ticket, allTickets);
    } else {
        // Collection/joined trip modes have their own distribution logic
        max = calculateBaseMax(ticket);
    }

    // Apply collection mode distribution if applicable
    if (context.hasCollectionMode && context.maxEntrantsPerBooking !== null) {
        const collectionMax = calculateCollectionMax(ticket, allTickets, context);
        max = Math.min(max, collectionMax);
    }

    // SB-933: in collection mode, also distribute the merged-PI capacity as a
    // TOTAL across siblings. This is the binding cap when Bilberry returns a
    // per-entry capacity tighter than collection-level max_entrants(/_per_booking)
    // — i.e. when an OTHER tour has already eaten into the shared resource.
    if (context.hasCollectionMode && context.sharedResourceCapacity !== null) {
        const resourceMax = calculateCollectionResourceMax(ticket, allTickets, context);
        max = Math.min(max, resourceMax);
    }

    // Apply joined trip capacity if applicable
    if (context.hasJoinedTrip && context.globalMaxEntrants !== null) {
        const joinedTripMax = calculateJoinedTripMax(ticket, allTickets, context);
        max = Math.min(max, joinedTripMax);
    }

    return max;
};

export const calculateActivityMin = (
    ticket: TicketOptionWithQuantity,
    allTickets?: TicketOptionWithQuantity[],
): number => {
    // If allTickets provided, use shared instance calculation
    if (allTickets && allTickets.length > 1) {
        return calculateSharedInstanceMin(ticket, allTickets);
    }
    // Otherwise use base calculation
    return calculateMin(ticket);
};

export const updateActivityQuantity = (
    tickets: TicketOptionWithQuantity[],
    ticketCategoryId: string,
    newQuantity: number,
): TicketOptionWithQuantity[] => {
    return tickets.map((ticket) =>
        ticket.ticketCategoryId === ticketCategoryId
            ? { ...ticket, quantity: newQuantity }
            : ticket,
    );
};

export const toActivityInputItems = (
    tickets: TicketOptionWithQuantity[],
    context: ValidationContext,
    translations: Translations,
    errors?: QuantityError[],
): QuantityInputItem[] => {
    // Compute errors if not provided
    const validationErrors = errors ?? validateActivityQuantities(tickets, context);

    return tickets.map((ticket) => ({
        id: ticket.ticketCategoryId,
        label: capitalize(getTicketName(ticket, translations)),
        value: ticket.quantity,
        min: 0, // Always allow reducing to 0 - validation will show errors if needed
        max: calculateActivityMax(ticket, tickets, context),
        subText: getAgeRangeText(ticket, translations),
        error: getErrorForTicket(ticket.ticketCategoryId, validationErrors, ticket, translations),
        productOptionId: ticket.productOptionId,
    }));
};
